import {
    PurchasePriceSummary,
    DeferredVariablePaymentType,
    CorporateEquityType,
    DividendPaymentTiming,
    DEFERRED_FIXED_PAYMENT_COLUMN_LABELS,
    DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS,
    JV_EQUITY_COLUMN_LABELS,
    CORPORATE_EQUITY_COMMON_COLUMN_LABELS,
    CORPORATE_EQUITY_PREFERRED_COLUMN_LABELS,
    YES_VALUE,
    PutCallRight,
} from '../../../../../slices/marketplace/loi-form/term-form';
import type { DetailColumnDef, DetailRow, CellHidingRule } from '../../../../Admin/BuyerMarketplace/OpportunityDetails/LoiSubmissionPackage/LoiAndTermsForm/TermsFormDetailBlock';
import {
    DFP_COLUMN_DEFS,
    DVP_COLUMN_DEFS,
    JV_COLUMN_DEFS,
    CORP_COMMON_COLUMN_DEFS,
    CORP_PREFERRED_COLUMN_DEFS,
    dfpRowToKeyValue,
    dvpRowToKeyValue,
    jvRowToKeyValue,
    corpCommonRowToKeyValue,
    corpPreferredRowToKeyValue,
    totalDfp,
    totalDvp,
} from '../../../../Admin/BuyerMarketplace/OpportunityDetails/LoiSubmissionPackage/LoiAndTermsForm/termsFormDetailColumns';

const DFP_HIDING_RULES: CellHidingRule[] = [
    { column: DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.contingencyDependentVariable, dependsOnColumn: DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.contingent, visibleWhen: YES_VALUE },
    { column: DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.contDvMinimumValue, dependsOnColumn: DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.contingent, visibleWhen: YES_VALUE },
    { column: DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.tailPeriodForEmployment, dependsOnColumn: DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.employmentContingency, visibleWhen: YES_VALUE },
];

const DVP_HIDING_RULES: CellHidingRule[] = [
    { column: DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.tailPeriodForEmploymentContingency, dependsOnColumn: DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.employmentContingency, visibleWhen: YES_VALUE },
    { column: DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.putCallExplanation, dependsOnColumn: DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.putCallRight, hiddenWhen: PutCallRight.NA },
];

const JV_HIDING_RULES: CellHidingRule[] = [
    { column: JV_EQUITY_COLUMN_LABELS.tailPeriodForEmploymentContingency, dependsOnColumn: JV_EQUITY_COLUMN_LABELS.employmentContingency, visibleWhen: YES_VALUE },
    { column: JV_EQUITY_COLUMN_LABELS.putCallExplanation, dependsOnColumn: JV_EQUITY_COLUMN_LABELS.putCallRight, hiddenWhen: PutCallRight.NA },
    { column: JV_EQUITY_COLUMN_LABELS.buyerCalculationPeriod, dependsOnColumn: JV_EQUITY_COLUMN_LABELS.putCallRight, hiddenWhen: PutCallRight.NA },
    { column: JV_EQUITY_COLUMN_LABELS.sellerCalculationResponsePeriod, dependsOnColumn: JV_EQUITY_COLUMN_LABELS.putCallRight, hiddenWhen: PutCallRight.NA },
    { column: JV_EQUITY_COLUMN_LABELS.paymentDate, dependsOnColumn: JV_EQUITY_COLUMN_LABELS.putCallRight, hiddenWhen: PutCallRight.NA },
];

const CORP_COMMON_HIDING_RULES: CellHidingRule[] = [
    { column: CORPORATE_EQUITY_COMMON_COLUMN_LABELS.contingencyDependentVariable, dependsOnColumn: CORPORATE_EQUITY_COMMON_COLUMN_LABELS.contingent, visibleWhen: YES_VALUE },
    { column: CORPORATE_EQUITY_COMMON_COLUMN_LABELS.contDvMinimumValue, dependsOnColumn: CORPORATE_EQUITY_COMMON_COLUMN_LABELS.contingent, visibleWhen: YES_VALUE },
    { column: CORPORATE_EQUITY_COMMON_COLUMN_LABELS.tailPeriodForEmploymentContingency, dependsOnColumn: CORPORATE_EQUITY_COMMON_COLUMN_LABELS.employmentContingency, visibleWhen: YES_VALUE },
    { column: CORPORATE_EQUITY_COMMON_COLUMN_LABELS.putCallExplanation, dependsOnColumn: CORPORATE_EQUITY_COMMON_COLUMN_LABELS.putCallRight, hiddenWhen: PutCallRight.NA },
    { column: CORPORATE_EQUITY_COMMON_COLUMN_LABELS.buyerCalculationPeriod, dependsOnColumn: CORPORATE_EQUITY_COMMON_COLUMN_LABELS.putCallRight, hiddenWhen: PutCallRight.NA },
    { column: CORPORATE_EQUITY_COMMON_COLUMN_LABELS.sellerCalculationResponsePeriod, dependsOnColumn: CORPORATE_EQUITY_COMMON_COLUMN_LABELS.putCallRight, hiddenWhen: PutCallRight.NA },
];

const CORP_PREFERRED_HIDING_RULES: CellHidingRule[] = [
    { column: CORPORATE_EQUITY_PREFERRED_COLUMN_LABELS.compoundingFrequency, dependsOnColumn: CORPORATE_EQUITY_PREFERRED_COLUMN_LABELS.dividendPaymentTiming, visibleWhen: DividendPaymentTiming.Cumulative },
    { column: CORPORATE_EQUITY_PREFERRED_COLUMN_LABELS.conversionRatio, dependsOnColumn: CORPORATE_EQUITY_PREFERRED_COLUMN_LABELS.convertibleIntoCommon, visibleWhen: YES_VALUE },
    { column: CORPORATE_EQUITY_PREFERRED_COLUMN_LABELS.contingencyDependentVariable, dependsOnColumn: CORPORATE_EQUITY_PREFERRED_COLUMN_LABELS.contingent, visibleWhen: YES_VALUE },
    { column: CORPORATE_EQUITY_PREFERRED_COLUMN_LABELS.contDvMinimumValue, dependsOnColumn: CORPORATE_EQUITY_PREFERRED_COLUMN_LABELS.contingent, visibleWhen: YES_VALUE },
    { column: CORPORATE_EQUITY_PREFERRED_COLUMN_LABELS.tailPeriodForEmploymentContingency, dependsOnColumn: CORPORATE_EQUITY_PREFERRED_COLUMN_LABELS.employmentContingency, visibleWhen: YES_VALUE },
    { column: CORPORATE_EQUITY_PREFERRED_COLUMN_LABELS.putCallExplanation, dependsOnColumn: CORPORATE_EQUITY_PREFERRED_COLUMN_LABELS.putCallRight, hiddenWhen: PutCallRight.NA },
    { column: CORPORATE_EQUITY_PREFERRED_COLUMN_LABELS.buyerCalculationPeriod, dependsOnColumn: CORPORATE_EQUITY_PREFERRED_COLUMN_LABELS.putCallRight, hiddenWhen: PutCallRight.NA },
    { column: CORPORATE_EQUITY_PREFERRED_COLUMN_LABELS.sellerCalculationResponsePeriod, dependsOnColumn: CORPORATE_EQUITY_PREFERRED_COLUMN_LABELS.putCallRight, hiddenWhen: PutCallRight.NA },
];

export interface DeepDiveDetailViewProps {
    title: string;
    columns: DetailColumnDef[];
    rows: DetailRow[];
    displayTotal?: number | null;
    showTotalRow?: boolean;
    singleRow?: boolean;
    showPaymentNumber?: boolean;
    paymentNumberHeader?: string;
    cellHidingRules?: CellHidingRule[];
}

export type DeepDiveBuilder = (summary: PurchasePriceSummary | null | undefined) => DeepDiveDetailViewProps;

export const DEEP_DIVE_REGISTRY: Record<string, DeepDiveBuilder | undefined> = {
    'Cash at Close': undefined,
    'Deferred Fixed Payments': (summary) => ({
        title: 'Deferred Fixed Payments',
        columns: DFP_COLUMN_DEFS,
        rows: (summary?.deferredFixedPayments?.rows ?? []).map(dfpRowToKeyValue),
        displayTotal: totalDfp(summary?.deferredFixedPayments?.rows ?? []),
        showTotalRow: true,
        showPaymentNumber: true,
        paymentNumberHeader: '',
        cellHidingRules: DFP_HIDING_RULES,
    }),
    'Earnout': (summary) => {
        const filtered = (summary?.deferredVariablePayments?.rows ?? [])
            .filter((r) => r.type === DeferredVariablePaymentType.Earnout);
        return {
            title: 'Earnout',
            columns: DVP_COLUMN_DEFS,
            rows: filtered.map(dvpRowToKeyValue),
            displayTotal: totalDvp(filtered),
            showTotalRow: true,
            showPaymentNumber: true,
            paymentNumberHeader: '',
            cellHidingRules: DVP_HIDING_RULES,
        };
    },
    'Synthetic JV Equity': (summary) => {
        const filtered = (summary?.deferredVariablePayments?.rows ?? [])
            .filter((r) => r.type === DeferredVariablePaymentType.Synthetic);
        return {
            title: 'Synthetic JV Equity',
            columns: DVP_COLUMN_DEFS,
            rows: filtered.map(dvpRowToKeyValue),
            displayTotal: totalDvp(filtered),
            showTotalRow: true,
            showPaymentNumber: true,
            paymentNumberHeader: '',
            cellHidingRules: DVP_HIDING_RULES,
        };
    },
    'JV Equity': (summary) => ({
        title: 'JV Equity',
        columns: JV_COLUMN_DEFS,
        rows: summary?.jvEquity?.row ? [jvRowToKeyValue(summary.jvEquity.row)] : [],
        displayTotal: Number(summary?.jvEquity?.row?.valueAtClosing) || 0,
        showTotalRow: true,
        singleRow: true,
        cellHidingRules: JV_HIDING_RULES,
    }),
    'Corporate Equity': undefined,
    'Common Corp Equity': (summary) => {
        const row = (summary?.corporateEquity?.rows ?? []).find((r) => r.type === CorporateEquityType.Common);
        return {
            title: 'Common Corp Equity',
            columns: CORP_COMMON_COLUMN_DEFS,
            rows: row ? [corpCommonRowToKeyValue(row)] : [],
            displayTotal: Number(row?.valueAtClosing) || 0,
            showTotalRow: true,
            singleRow: true,
            cellHidingRules: CORP_COMMON_HIDING_RULES,
        };
    },
    'Preferred Corp Equity': (summary) => {
        const row = (summary?.corporateEquity?.rows ?? []).find((r) => r.type === CorporateEquityType.Preferred);
        return {
            title: 'Preferred Corp Equity',
            columns: CORP_PREFERRED_COLUMN_DEFS,
            rows: row ? [corpPreferredRowToKeyValue(row)] : [],
            displayTotal: Number(row?.valueAtClosing) || 0,
            showTotalRow: true,
            singleRow: true,
            cellHidingRules: CORP_PREFERRED_HIDING_RULES,
        };
    },
    'Other': undefined,
};

export const getFirstDeepDiveRowLabel = (
    summary: PurchasePriceSummary | null | undefined,
): string | null => {
    for (const [label, builder] of Object.entries(DEEP_DIVE_REGISTRY)) {
        if (!builder) {
            continue;
        }
        if ((builder(summary).displayTotal ?? 0) > 0) {
            return label;
        }
    }
    return null;
};
